// Reads the stats like an operator would: is the business healthy, and where are people getting
// stuck? Every number is checked against a target, and every step of both funnels is checked for
// people dropping out or waiting too long. Problems are ranked by how many people they cost.
// Plain data, so the admin page, an agent or a weekly text can all use it.
import type { Stats } from './stats.ts';
import { brand } from './brand.ts';

export type Status = 'good' | 'watch' | 'bad' | 'unknown';

export interface HealthMetric {
  key: string;
  label: string;
  value: string;
  target: string;
  status: Status;
  /** Why this number matters, in a sentence. */
  why: string;
}

export interface Bottleneck {
  /** Where it happens, e.g. "Payer: Shared the code → A friend joined". */
  where: string;
  title: string;
  /** The numbers behind it. */
  evidence: string;
  /** What to try, most likely fix first. */
  fixes: string[];
  severity: 'high' | 'medium' | 'low';
  /** People affected per period, roughly. Used to rank. */
  score: number;
}

export interface Health {
  verdict: 'healthy' | 'okay' | 'needs work' | 'at risk' | 'too early';
  headline: string;
  metrics: HealthMetric[];
  bottlenecks: Bottleneck[];
  /** Fewer people than this through a step and its numbers are noise. */
  minSample: number;
}

const MIN_SAMPLE = 5;

/** Higher is better: good at or above `good`, watch at or above `watch`. */
const atLeast = (v: number | null, good: number, watch: number): Status => (v === null ? 'unknown' : v >= good ? 'good' : v >= watch ? 'watch' : 'bad');
/** Lower is better. */
const atMost = (v: number | null, good: number, watch: number): Status => (v === null ? 'unknown' : v <= good ? 'good' : v <= watch ? 'watch' : 'bad');

export function dur(secs: number | null | undefined): string {
  if (secs === null || secs === undefined || !Number.isFinite(secs)) return '–';
  if (secs < 90) return `${Math.round(secs)}s`;
  const m = secs / 60;
  if (m < 90) return `${Math.round(m)}m`;
  const h = m / 60;
  if (h < 48) return `${Math.round(h * 10) / 10}h`;
  return `${Math.round((h / 24) * 10) / 10}d`;
}
const pct = (n: number | null) => (n === null ? '–' : `${n}%`);
const ratio = (a: number, b: number) => (b ? Math.round((a / b) * 100) : null);

/**
 * What each step should convert at and how long it should take, and what to try when it doesn't.
 * Targets are where a dinner tool should be, not industry averages: tune them as real data arrives.
 */
const STEPS: Record<string, { minPct: number; maxSecs: number; slow: string[]; drop: string[] }> = {
  'Started a split': {
    minPct: 40, maxSecs: Infinity,
    slow: [],
    drop: [
      'The home page has to sell the idea in one screen: show a 10-second demo of a receipt splitting itself.',
      'Add "Try it with a sample receipt" so people can see it work before dinner.',
      'Some home visitors are friends coming back after paying. Count them separately before judging this.',
    ],
  },
  'Checked the receipt': {
    minPct: 80, maxSecs: 90,
    slow: [
      'Look at the scan time in Receipt reading. If reading is slow, try a faster model for the first pass.',
      'Flag only the lines that need a look instead of making people check every line.',
    ],
    drop: [
      'Check Receipt reading for failures. A failed scan usually means a lost split.',
      'Make the "doesn\'t add up" fixes one tap: add as fee, add a line, read again.',
      'Let people skip checking and fix lines later from the dashboard.',
    ],
  },
  'Shared the code': {
    minPct: 85, maxSecs: 60,
    slow: [
      'The Venmo/Cash App step is the slow part. Remember handles (already done) and prefill the name.',
      'Move tip and thanks choices after sharing so the code goes up sooner.',
    ],
    drop: [
      'People abandon on the payment details screen. Ask for one handle, not three.',
      `Explain that friends pay them directly. Some may think ${brand.name} holds money.`,
    ],
  },
  'A friend joined': {
    minPct: 85, maxSecs: 300,
    slow: [
      'Show a "Text the link to the table" button next to the QR. Not everyone can scan across a table.',
      'Make the QR bigger and brighter, with "Point your camera here" in large text.',
    ],
    drop: [
      'Friends aren\'t scanning. Add a share button for group texts and AirDrop.',
      'Let the payer add people by name and claim for them (already possible). Make it more obvious.',
    ],
  },
  'Closed the split': {
    minPct: 75, maxSecs: 3600,
    slow: [
      'Remind the payer to close once everyone has claimed, or once nothing is left unclaimed.',
      'Close automatically when every item is claimed and everyone has joined.',
    ],
    drop: [
      'Payers forget to close. Send a reminder an hour after dinner.',
      'Explain why closing matters: it locks the amounts so people can pay.',
    ],
  },
  'Everyone paid': {
    minPct: 70, maxSecs: 86_400,
    slow: [
      'Add one-tap reminder texts for whoever hasn\'t paid, with the exact amount and a pay link.',
      'Remind friends automatically the next morning (needs their phone number and texting set up).',
    ],
    drop: [
      'Payment links must open Venmo/Cash App with the amount filled in. Check they work on Android too.',
      'Let the payer mark cash payments in one tap so settled splits really look settled.',
    ],
  },
  'Joined': {
    minPct: 75, maxSecs: 60,
    slow: ['Cut the join form to the name only, and ask for the phone after they claim.'],
    drop: [
      'Friends open the link but don\'t join. Show the items before asking for their name.',
      'Make "Tap your name" more obvious for people the payer already added.',
    ],
  },
  'Claimed an item': {
    minPct: 90, maxSecs: 60,
    slow: ['Put the most likely items first, or let people search for them.'],
    drop: ['Friends join but claim nothing. They may think the payer is handling it. Say "Tap what you had" louder.'],
  },
  'Paid': {
    minPct: 85, maxSecs: 21_600,
    slow: [
      'Show the pay button right after claiming, with the exact amount.',
      'Remind them with a text if they haven\'t paid within a few hours.',
    ],
    drop: [
      'Friends claim but don\'t pay. Check the pay links open the app with the amount filled in.',
      'Make "I paid" one tap once they come back from Venmo.',
    ],
  },
};

function funnelBottlenecks(steps: Stats['funnels']['payer'], who: 'Payer' | 'Friend'): Bottleneck[] {
  const out: Bottleneck[] = [];
  for (let i = 1; i < steps.length; i++) {
    const prev = steps[i - 1], step = steps[i], rule = STEPS[step.step];
    if (!rule || prev.count < MIN_SAMPLE) continue;
    const conv = Math.round((step.count / prev.count) * 100);
    const where = `${who}: ${prev.step} → ${step.step}`;
    if (conv < rule.minPct) {
      const lost = prev.count - step.count;
      const gap = rule.minPct - conv;
      out.push({
        where,
        title: `${lost} of ${prev.count} stop before "${step.step.toLowerCase()}"`,
        evidence: `${conv}% make it, target ${rule.minPct}%.`,
        fixes: rule.drop,
        severity: gap >= 25 ? 'high' : gap >= 10 ? 'medium' : 'low',
        // People who would have made it at the target rate.
        score: Math.round((prev.count * gap) / 100),
      });
    }
    if (step.secs !== null && step.count >= MIN_SAMPLE && step.secs > rule.maxSecs) {
      const times = step.secs / rule.maxSecs;
      out.push({
        where,
        title: `"${step.step}" takes ${dur(step.secs)}`,
        evidence: `Median ${dur(step.secs)} from the step before, target under ${dur(rule.maxSecs)}.`,
        fixes: rule.slow,
        severity: times >= 3 ? 'high' : times >= 1.5 ? 'medium' : 'low',
        // Slowness costs less than people leaving outright, so it ranks lower per person.
        score: Math.round(step.count * Math.min(1, (times - 1) / 4)),
      });
    }
  }
  return out;
}

export function assessHealth(s: Stats): Health {
  const k = s.kpis, sc = s.scans, lo = s.loyalty;
  const started = s.funnels.payer.find((x) => x.step === 'Started a split')?.count ?? 0;
  const shared = s.funnels.payer.find((x) => x.step === 'Shared the code')?.count ?? 0;
  const growth = k.prevSplits !== null && k.prevSplits >= 3 ? Math.round(((k.splits - k.prevSplits) / k.prevSplits) * 100) : null;
  const scanGood = sc.total >= MIN_SAMPLE ? ratio(sc.ok - sc.stillOff, sc.total) : null;
  const repeatRate = lo.payers >= MIN_SAMPLE ? ratio(lo.repeatPayers, lo.payers) : null;
  const viral = s.splits.friendsJoined >= 20 ? ratio(lo.friendsWhoLaterPaidABill, s.splits.friendsJoined) : null;
  const enough = (n: number, v: number | null) => (n >= MIN_SAMPLE ? v : null);

  const metrics: HealthMetric[] = [
    {
      key: 'growth', label: 'Growth', value: growth === null ? `${k.splits} splits` : `${growth > 0 ? '+' : ''}${growth}%`,
      target: '+10% a period', status: atLeast(growth, 10, -10),
      why: 'More splits than the period before. Needs a date range and 3+ splits before it to judge.',
    },
    {
      key: 'activation', label: 'Payers who finish', value: pct(enough(started, ratio(shared, started))),
      target: '70%+', status: atLeast(enough(started, ratio(shared, started)), 70, 50),
      why: 'Of people who start a split, how many get the code up for friends.',
    },
    {
      key: 'speed', label: 'Scan to code shared', value: dur(k.medianSecsScanToShare),
      target: 'under 3 min', status: atMost(k.medianSecsScanToShare, 180, 300),
      why: 'The table is waiting. Every minute here is a reason to just let one person pay.',
    },
    {
      key: 'coverage', label: 'Table on the split', value: pct(enough(k.tableCoverageSplits, k.tableCoveragePct)),
      target: '80%+', status: atLeast(enough(k.tableCoverageSplits, k.tableCoveragePct), 80, 60),
      why: 'Of the seats the payer counted, how many ended up on the split. The rest are money the payer chases by hand.',
    },
    {
      key: 'payback', label: 'Friends who paid', value: pct(enough(s.payments.friends, k.paidPct)),
      target: '85%+', status: atLeast(enough(s.payments.friends, k.paidPct), 85, 70),
      why: 'The promise of the product: the payer gets their money back.',
    },
    {
      key: 'settle', label: 'Time to everyone paid', value: dur(k.medianMinsToAllPaid === null ? null : k.medianMinsToAllPaid * 60),
      target: 'under 24h', status: atMost(k.medianMinsToAllPaid, 1440, 4320),
      why: 'How long the payer is out of pocket.',
    },
    {
      key: 'scans', label: 'Receipts read right', value: pct(scanGood),
      target: '90%+', status: atLeast(scanGood, 90, 80),
      why: 'Read and adding up to the printed total. A bad scan means typing or giving up.',
    },
    {
      key: 'retention', label: 'Payers who come back', value: pct(repeatRate),
      target: '25%+', status: atLeast(repeatRate, 25, 10),
      why: 'Payers with 2+ splits, all time. People only pick up the bill a few times a year, so this grows slowly.',
    },
    {
      key: 'viral', label: 'Friends who become payers', value: pct(viral),
      target: '5%+', status: atLeast(viral, 5, 2),
      why: 'Each dinner shows the app to 3–6 people. This is how it grows without ads.',
    },
  ];

  // Business-level problems that aren't a single funnel step.
  const extra: Bottleneck[] = [];
  if (sc.total >= MIN_SAMPLE && scanGood !== null && scanGood < 90) {
    extra.push({
      where: 'Receipt reading',
      title: `${sc.total - (sc.ok - sc.stillOff)} of ${sc.total} receipts failed or didn't add up`,
      evidence: `${scanGood}% read right, target 90%. ${sc.failed} failed outright, ${sc.stillOff} didn't add up after a second look.`,
      fixes: [
        'Run the receipt eval on the failing kinds of receipts and fix the prompt or the clean-up rules.',
        'Ask for a retake when the photo is blurry or cut off, before reading it.',
      ],
      severity: scanGood < 75 ? 'high' : scanGood < 85 ? 'medium' : 'low',
      score: Math.round((sc.total * (90 - scanGood)) / 100),
    });
  }
  if (k.tableCoverageSplits >= MIN_SAMPLE && k.tableCoveragePct !== null && k.tableCoveragePct < 80) {
    extra.push({
      where: 'At the table',
      title: `Only ${k.tableCoveragePct}% of the table ends up on the split`,
      evidence: `Across ${k.tableCoverageSplits} splits where the payer said how many were there.`,
      fixes: [
        'After sharing, show "3 of 6 joined" with a button to text the link to the rest.',
        'Make adding someone by name, and claiming for them, one obvious step.',
      ],
      severity: k.tableCoveragePct < 60 ? 'high' : 'medium',
      score: Math.round(((80 - k.tableCoveragePct) / 100) * s.kpis.people),
    });
  }
  if (repeatRate !== null && repeatRate < 10 && lo.payers >= 10) {
    extra.push({
      where: 'Coming back',
      title: `Only ${repeatRate}% of payers have split more than once`,
      evidence: `${lo.repeatPayers} of ${lo.payers} payers.`,
      fixes: [
        'Let payers save the site to their home screen right after their first split settles.',
        'Text the payer a link next time they\'re at a restaurant they used before (needs their consent).',
      ],
      severity: 'medium',
      score: Math.round(lo.payers * 0.1),
    });
  }

  if (growth !== null && growth < -10 && k.prevSplits !== null) {
    const top = s.sources[0];
    extra.push({
      where: 'Growth',
      title: `Splits are down ${-growth}% on the period before`,
      evidence: `${k.splits} splits vs ${k.prevSplits} before.${top ? ` Most splits come from ${top.source}.` : ''}`,
      fixes: [
        'Check Where people came from: did a source dry up, or a post stop running?',
        'Slow weeks happen (holidays, weather). Compare the same days last month before reacting.',
        'Ask your best repeat payers what would make them use it at every dinner.',
      ],
      severity: growth <= -30 ? 'high' : 'medium',
      score: k.prevSplits - k.splits,
    });
  }
  if (viral !== null && viral < 2) {
    extra.push({
      where: 'Growth loop',
      title: `Friends aren't going on to pay a bill themselves (${viral}%)`,
      evidence: `${lo.friendsWhoLaterPaidABill} of ${s.splits.friendsJoined} friends who joined later started their own split. Target 5%.`,
      fixes: [
        'On the "you\'re all paid" screen, show friends "Got the bill next time? Split it in 2 minutes" with a start button.',
        `Offer to save ${brand.name} to their home screen right after they pay.`,
        `Put a small "Split with ${brand.name}" line in the payer's reminder texts.`,
      ],
      severity: 'medium',
      score: Math.round((s.splits.friendsJoined * (5 - viral)) / 100),
    });
  }

  const bottlenecks = [...funnelBottlenecks(s.funnels.payer, 'Payer'), ...funnelBottlenecks(s.funnels.friend, 'Friend'), ...extra]
    .sort((a, b) => b.score - a.score || (a.severity === 'high' ? -1 : 1));

  const known = metrics.filter((m) => m.status !== 'unknown');
  const bad = known.filter((m) => m.status === 'bad').length;
  const watch = known.filter((m) => m.status === 'watch').length;
  const verdict: Health['verdict'] = known.length < 3 ? 'too early'
    : bad >= 3 ? 'at risk'
      : bad ? 'needs work'
        : watch ? 'okay'
          : 'healthy';
  const top = bottlenecks[0];
  const headline = verdict === 'too early'
    ? `Not enough data yet to judge. Most checks need ${MIN_SAMPLE}+ splits or scans in this range.`
    : `${known.length - bad - watch} of ${known.length} checks on target${bad ? `, ${bad} off target` : ''}${watch ? `, ${watch} to watch` : ''}.`
      + (top ? ` Biggest bottleneck, ${top.where}: ${top.title.charAt(0).toLowerCase() + top.title.slice(1)}.` : ' No bottlenecks found.');

  return { verdict, headline, metrics, bottlenecks, minSample: MIN_SAMPLE };
}
